import { useState, useEffect, useCallback } from 'react';
import { useAuthStore } from '../stores/authStore';
import { driveService } from '../services/driveService';
import { DriveFile, DriveStats, DriveFilters, DriveFolder } from '../types/drive';

interface UseDriveFilesOptions {
  targetUserId?: string; // Per admin/moderatori che vogliono vedere file di altri utenti
}

export const useDriveFiles = (options: UseDriveFilesOptions = {}) => {
  const { user, userProfile } = useAuthStore();
  const [files, setFiles] = useState<DriveFile[]>([]);
  const [stats, setStats] = useState<DriveStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<DriveFilters>({});

  const targetUserId = options.targetUserId || user?.uid;
  const isAdmin = userProfile?.role === 'admin';
  const isModerator = userProfile?.role === 'moderator';
  const canAccessAllFiles = isAdmin || isModerator;

  // Verifica permessi
  const canAccessUserFiles = useCallback((userId: string): boolean => {
    if (!user?.uid) return false;
    if (canAccessAllFiles) return true;
    return user.uid === userId;
  }, [user?.uid, canAccessAllFiles]);

  // Carica file
  const loadFiles = useCallback(async () => {
    if (!targetUserId || !canAccessUserFiles(targetUserId)) {
      setError('Non hai i permessi per accedere a questi file');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      let filesData: DriveFile[];
      if (canAccessAllFiles && options.targetUserId) {
        // Admin/moderatore che vuole vedere file di utente specifico
        filesData = await driveService.getUserFiles(targetUserId);
      } else if (canAccessAllFiles) {
        // Admin/moderatore che vuole vedere tutti i file
        filesData = await driveService.getAllFiles();
      } else {
        // Utente normale che vede solo i propri file
        filesData = await driveService.getUserFiles(targetUserId);
      }

      setFiles(filesData);
    } catch (err) {
      console.error('Errore nel caricamento file:', err);
      setError(err instanceof Error ? err.message : 'Errore nel caricamento file');
    } finally {
      setLoading(false);
    }
  }, [targetUserId, canAccessAllFiles, options.targetUserId]);

  // Carica statistiche
  const loadStats = useCallback(async () => {
    if (!targetUserId || !canAccessUserFiles(targetUserId)) {
      return;
    }

    try {
      const statsData = await driveService.getUserStats(targetUserId);
      setStats(statsData);
    } catch (err) {
      console.error('Errore nel caricamento statistiche:', err);
    }
  }, [targetUserId, canAccessUserFiles]);

  // Filtra file
  const filteredFiles = files.filter(file => {
    // Filtro per cartella
    if (filters.folder && file.folder !== filters.folder) {
      return false;
    }

    // Filtro per ricerca
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      const fileNameLower = file.originalName.toLowerCase();
      if (!fileNameLower.includes(searchLower)) {
        return false;
      }
    }

    return true;
  });

  // Ordina file
  const sortedFiles = [...filteredFiles].sort((a, b) => {
    if (!filters.sortBy) return 0;

    const order = filters.sortOrder === 'desc' ? -1 : 1;

    switch (filters.sortBy) {
      case 'name':
        return a.originalName.localeCompare(b.originalName) * order;
      case 'date':
        return (a.uploadedAt.seconds - b.uploadedAt.seconds) * order;
      case 'size':
        return (a.fileSize - b.fileSize) * order;
      default:
        return 0;
    }
  });

  // Carica file e statistiche all'avvio
  useEffect(() => {
    if (targetUserId) {
      loadFiles();
      loadStats();
    }
  }, [targetUserId]); // Rimuoviamo loadFiles e loadStats dalle dipendenze per evitare loop infiniti

  // Listener in tempo reale per file
  useEffect(() => {
    if (!targetUserId || !canAccessUserFiles(targetUserId)) {
      return;
    }

    let unsubscribe: (() => void) | undefined;

    try {
      if (canAccessAllFiles && options.targetUserId) {
        // Admin/moderatore che vuole vedere file di utente specifico
        unsubscribe = driveService.subscribeToUserFiles(targetUserId, setFiles);
      } else if (!canAccessAllFiles) {
        // Utente normale che vede solo i propri file
        unsubscribe = driveService.subscribeToUserFiles(targetUserId, setFiles);
      }
    } catch (error) {
      console.error('Errore nel setup listener file:', error);
    }

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [targetUserId, canAccessAllFiles, options.targetUserId]);

  // Listener in tempo reale per statistiche
  useEffect(() => {
    if (!targetUserId || !canAccessUserFiles(targetUserId)) {
      return;
    }

    const unsubscribe = driveService.subscribeToUserStats(targetUserId, setStats);

    return () => {
      unsubscribe();
    };
  }, [targetUserId, canAccessUserFiles]);

  // Funzioni di utilità
  const updateFilters = useCallback((newFilters: Partial<DriveFilters>) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  }, []);

  const clearFilters = useCallback(() => {
    setFilters({});
  }, []);

  const getFilesByFolder = useCallback((folder: DriveFolder) => {
    return files.filter(file => file.folder === folder);
  }, [files]);

  const getTotalSize = useCallback(() => {
    return files.reduce((total, file) => total + file.fileSize, 0);
  }, [files]);

  return {
    // Stato
    files: sortedFiles,
    stats,
    loading,
    error,
    filters,

    // Funzioni
    loadFiles,
    loadStats,
    updateFilters,
    clearFilters,
    getFilesByFolder,
    getTotalSize,

    // Permessi
    canAccessAllFiles,
    isAdmin,
    isModerator,
  };
}; 